import React from "react";
import { Switch, Route, Redirect, useLocation, matchPath } from "react-router-dom";
import { PrivateRoute } from "./components/PrivateRoute";

import {
    SignInScreen,
    ForgotPassword,
    Bars,
    BarDetail,
    BarSpecials,
    Users,
    BarManagers,
    Promotions,
    PromotionDetail,
    Regions,
    RegionDetail,
    Placements,
    Analytics,
    LocalAds,
    FeedAds,
    VerifyPromotionPayment,
    VerifyFeaturePlacementPayment,
    TrialActivatedConfirmation,
    PromotionActivatedConfirmation,
    HowTo,
    HowToEditor,
    MigrationAdmin,
    OSMDataAdmin,
    CategoryDefinitions,
    ClaimBar,
    ClaimedBarReview,
    ContactedBars
} from "./views";

import { withLayout } from "./components/Layout";
import { Header } from "./header/Header";
import { Navigation } from "./navigation/Navigation";
import { useUserRoles } from "./globalstore";
import { auth } from "./services/firebase";
import { LoadingBlanket } from "./components/LoadingBlanket";
import { FeedAdsProvider } from "./actions/feedAds";

const withBasicLayout = withLayout(Header)(Navigation);

const BarListScreen = withBasicLayout(Bars, { title: "Manage Bars" });
const ManageBarScreen = withBasicLayout(BarDetail, {
    title: "Manage bar details"
});
const ManageUsersScreen = withBasicLayout(Users, {
    title: "Manage users"
});
const ManageBarRolesScreen = withBasicLayout(BarManagers, {
    title: "Bar Managers"
});
const BarSpecialsScreen = withBasicLayout(BarSpecials, {
    title: "Manage Bar Specials"
});
const PromotionListScreen = withBasicLayout(Promotions, {
    title: "Manage Promotions"
});
const PromotionListScreenByBar = withBasicLayout(Promotions, {
    title: "Manage Promotions by Bar"
});
const ManagePromotionScreen = withBasicLayout(PromotionDetail, {
    title: "Manage Promotion Details"
});

const RegionListScreen = withBasicLayout(Regions, {
    title: "Regions"
});
const RegionDetailScreen = withBasicLayout(RegionDetail, {
    title: "Manage Regions"
});

const PlacementsScreen = withBasicLayout(Placements, {
    title: "Manage Featured Placements"
});

const AnalyticsScreen = withBasicLayout(Analytics, {
    title: "Analytics"
});

const VerifyPromotionPaymentScreen = withBasicLayout(VerifyPromotionPayment, {
    title: "Verify Promotion Payment"
});

const VerifyFeaturePlacementPaymentScreen = withBasicLayout(VerifyFeaturePlacementPayment, {
    title: "Verify Feature Placement Payment"
});

const TrialActivatedScreen = withBasicLayout(TrialActivatedConfirmation, {
    title: "Trial Activated"
});

const PromotionActivatedScreen = withBasicLayout(PromotionActivatedConfirmation, {
    title: "Promotion Activated"
});

const LocalAdsScreen = withBasicLayout(LocalAds, {
    title: "Local Advertisements"
});

const FeedAdsScreen = withBasicLayout(FeedAds, {
    title: "Feed Advertisements"
});

// New How To screens
const HowToScreen = withBasicLayout(HowTo, {
    title: "How To Guide"
});

const HowToEditorScreen = withBasicLayout(HowToEditor, {
    title: "How To Editor"
});

// New Admin screens
const MigrationAdminScreen = withBasicLayout(MigrationAdmin, {
    title: "Supabase Migration Admin"
});

const OSMDataAdminScreen = withBasicLayout(OSMDataAdmin, {
    title: "OSM Data Collection Admin"
});

const CategoryDefinitionsScreen = withBasicLayout(CategoryDefinitions, {
    title: "Category Definitions"
});

const ClaimBarScreen = withBasicLayout(ClaimBar, {
    title: "Claim Your Bar"
});

const ClaimedBarReviewScreen = withBasicLayout(ClaimedBarReview, {
    title: "Claimed Bar Review"
});

const ContactedBarsScreen = withBasicLayout(ContactedBars, {
    title: "Contacted Bars"
});

function App() {
    const location = useLocation();
    const PUBLIC_PATHS = ["/login", "/forgotpassword", "/claim-bar"];
    const isPublicRoute = PUBLIC_PATHS.some((path) =>
        matchPath(location.pathname, { path, exact: true })
    );
    const { userRoles: { isAdmin, roles, status } } = useUserRoles();

    if (isPublicRoute) {
        return (
            <Switch>
                <Route exact path="/login" component={SignInScreen} />
                <Route exact path="/forgotpassword" component={ForgotPassword} />
                <Route
                    exact
                    path="/claim-bar"
                    render={(props) =>
                        auth.currentUser ? (
                            <ClaimBarScreen {...props} />
                        ) : (
                            <ClaimBar {...props} />
                        )
                    }
                />
                <Redirect to="/login" />
            </Switch>
        );
    }
    if (status === "fetching") {
        return <LoadingBlanket />;
    }
    return (
        <FeedAdsProvider>
            <div className="App">
                <Switch>
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/users"
                        component={ManageUsersScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/bars"
                        component={BarListScreen}
                    />
                    <PrivateRoute
                        exact
                        path="/promotions"
                        component={PromotionListScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/regions"
                        component={RegionListScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/region/:id"
                        component={RegionDetailScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/local-ads"
                        component={LocalAdsScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/feed-ads"
                        component={FeedAdsScreen}
                    />
                    {/* How To routes */}
                    <PrivateRoute
                        exact
                        path="/how-to"
                        component={HowToScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/how-to-editor"
                        component={HowToEditorScreen}
                    />
                    {/* Admin Development Tools */}
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/migration"
                        component={MigrationAdminScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/osm-data"
                        component={OSMDataAdminScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/category-definitions"
                        component={CategoryDefinitionsScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/contacted-bars"
                        component={ContactedBarsScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/claimed-bar-review"
                        component={ClaimedBarReviewScreen}
                    />
                    <PrivateRoute
                        path="/bar/:id"
                        component={ManageBarScreen}
                    />
                    <PrivateRoute
                        path="/managers/:id"
                        component={ManageBarRolesScreen}
                    />
                    <PrivateRoute
                    path="/specials/:id"
                    component={BarSpecialsScreen}
                />
                <PrivateRoute
                        path="/promotions/:id"
                        component={PromotionListScreenByBar}
                    />
                    <PrivateRoute
                        path="/promotion/:id"
                        component={ManagePromotionScreen}
                    />
                    <PrivateRoute
                        path="/premium/placements/:id"
                        component={PlacementsScreen}
                    />
                    <PrivateRoute
                        path="/premium/analytics"
                        component={AnalyticsScreen}
                    />
                    <PrivateRoute
                        path="/verifyPromotionPayment"
                        component={VerifyPromotionPaymentScreen}
                    />
                    <PrivateRoute
                        path="/verifyFeaturePlacementPayment"
                        component={VerifyFeaturePlacementPaymentScreen}
                    />
                <PrivateRoute
                    path="/trial-activated/:barId/:promotionId" // Use params if needed on the page
                    component={TrialActivatedScreen}
                />
                <PrivateRoute
                    path="/promotion-activated/:barId/:promotionId"
                    component={PromotionActivatedScreen}
                />
                    <Route
                        path="/"
                        render={(props) => {
                            return status === "fetching" ? (
                                <LoadingBlanket />
                            ) : isAdmin ? (
                                <Redirect to="/bars" {...props} />
                            ) : roles && Object.keys(roles).length > 0 ? (
                                <Redirect
                                    to={`/bar/${Object.keys(roles)[0]}`}
                                    {...props}
                                />
                            ) : (
                                    <Redirect to="/login" {...props} />
                            );
                        }}
                    />
                </Switch>
            </div>
        </FeedAdsProvider>

    );
}

export default App;